import { useState, useEffect, useRef } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { canAccessFeature } from "../config/subscriptionPlans";
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  Timestamp 
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { ChatBubbleIcon } from "./icons/OutlineIcons";

interface Message {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  timestamp: Timestamp;
  instructorId: string;
}

export default function LiveChat() {
  const { user } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const userData = user as any;
  const userTier = userData?.subscriptionTier || "free";
  const userRole = userData?.role || "student";
  const instructorId = userRole === "instructor" ? userData.uid : userData?.instructorId;

  // Check if user can access live chat
  const canAccessChat = canAccessFeature(userTier, "liveChat") || userRole === "admin" || userRole === "moderator";

  useEffect(() => {
    if (!canAccessChat || !instructorId || !isOpen) return;

    const q = query(
      collection(db, "chatMessages"),
      where("instructorId", "==", instructorId),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Message[];
      setMessages(msgs);
      scrollToBottom();
    });

    return () => unsubscribe();
  }, [canAccessChat, instructorId, isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !user || !instructorId) return;

    setLoading(true);
    try {
      await addDoc(collection(db, "chatMessages"), {
        text: newMessage.trim(),
        senderId: user.uid,
        senderName: userData.name || userData.email,
        senderRole: userRole,
        instructorId: instructorId,
        timestamp: serverTimestamp(),
      });

      setNewMessage("");
      scrollToBottom();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  // Don't show chat if user doesn't have access
  if (!canAccessChat) {
    return null;
  }

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-primary text-white rounded-full shadow-2xl hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-50 flex items-center justify-center"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <ChatBubbleIcon className="w-6 h-6" />
        )}
        {messages.length > 0 && !isOpen && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {messages.length > 9 ? "9+" : messages.length}
          </span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-primary text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ChatBubbleIcon className="w-6 h-6" />
              <div>
                <h3 className="font-bold text-lg">Live Chat</h3>
                <p className="text-xs text-white/80">
                  {userRole === "instructor" ? "แชทกับนักเรียน" : "แชทกับผู้สอน"}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 hover:bg-white/20 rounded-lg transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 ? (
              <div className="text-center text-textLight py-12">
                <ChatBubbleIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">ยังไม่มีข้อความ</p>
                <p className="text-xs mt-1">เริ่มต้นการสนทนาได้เลย!</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isOwnMessage = msg.senderId === user?.uid;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        isOwnMessage
                          ? "bg-gradient-primary text-white"
                          : "bg-white text-textDark border border-gray-200"
                      }`}
                    >
                      {!isOwnMessage && (
                        <p className="text-xs font-semibold mb-1 opacity-70">
                          {msg.senderName}
                          {msg.senderRole === "admin" && " 👑"}
                          {msg.senderRole === "moderator" && " 🛡️"}
                        </p>
                      )}
                      <p className="text-sm break-words">{msg.text}</p>
                      <p className={`text-xs mt-1 ${isOwnMessage ? "text-white/70" : "text-textLight"}`}>
                        {msg.timestamp?.toDate().toLocaleTimeString("th-TH", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="พิมพ์ข้อความ..."
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors text-sm"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !newMessage.trim()}
                className="bg-gradient-primary text-white px-4 py-2 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
